import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";

export default function Stock() {
  const [balances, setBalances] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [whOpen, setWhOpen] = useState(false);
  const [whForm, setWhForm] = useState({ code: "", name: "", address: "", is_default: false });
  const [mvOpen, setMvOpen] = useState(false);
  const [mvForm, setMvForm] = useState({ item_id: "", warehouse_id: "", movement_type: "IN", quantity: 0, batch_number: "", to_warehouse_id: "", unit_cost: 0, notes: "" });
  const [movements, setMovements] = useState([]);

  const load = async () => {
    const [b, i, w, m] = await Promise.all([
      api.get("/inventory/stock"),
      api.get("/inventory/items"),
      api.get("/inventory/warehouses"),
      api.get("/inventory/movements", { params: { limit: 50 } }),
    ]);
    setBalances(b.data); setItems(i.data); setWarehouses(w.data); setMovements(m.data);
  };
  useEffect(() => { load(); }, []);

  const saveWh = async () => {
    try { await api.post("/inventory/warehouses", whForm); toast.success("Warehouse added"); setWhOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveMovement = async () => {
    try {
      await api.post("/inventory/movements", { ...mvForm, quantity: Number(mvForm.quantity), unit_cost: Number(mvForm.unit_cost),
        to_warehouse_id: mvForm.movement_type === "TRANSFER" ? mvForm.to_warehouse_id : null });
      toast.success(`${mvForm.movement_type} posted`); setMvOpen(false);
      setMvForm({ item_id: "", warehouse_id: "", movement_type: "IN", quantity: 0, batch_number: "", to_warehouse_id: "", unit_cost: 0, notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="stock-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Stock &amp; warehouses</h1>
          <p className="text-muted-foreground">Live balances across all warehouses and batches.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={whOpen} onOpenChange={setWhOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="new-wh-btn">+ Warehouse</Button></DialogTrigger>
            <DialogContent data-testid="wh-dialog">
              <DialogHeader><DialogTitle>New warehouse</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Code</Label><Input value={whForm.code} onChange={(e) => setWhForm({ ...whForm, code: e.target.value })} data-testid="wh-code" /></div>
                  <div className="space-y-2"><Label>Name</Label><Input value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} data-testid="wh-name" /></div>
                </div>
                <div className="space-y-2"><Label>Address</Label><Input value={whForm.address} onChange={(e) => setWhForm({ ...whForm, address: e.target.value })} /></div>
                <Button onClick={saveWh} data-testid="wh-save">Save warehouse</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={mvOpen} onOpenChange={setMvOpen}>
            <DialogTrigger asChild><Button data-testid="new-mv-btn"><Plus className="w-4 h-4 mr-1" /> Stock movement</Button></DialogTrigger>
            <DialogContent data-testid="mv-dialog">
              <DialogHeader><DialogTitle>New stock movement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Item</Label>
                    <Select value={mvForm.item_id} onValueChange={(v) => setMvForm({ ...mvForm, item_id: v })}>
                      <SelectTrigger data-testid="mv-item"><SelectValue placeholder="Pick item" /></SelectTrigger>
                      <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Warehouse</Label>
                    <Select value={mvForm.warehouse_id} onValueChange={(v) => setMvForm({ ...mvForm, warehouse_id: v })}>
                      <SelectTrigger data-testid="mv-wh"><SelectValue placeholder="Pick warehouse" /></SelectTrigger>
                      <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>Movement</Label>
                    <Select value={mvForm.movement_type} onValueChange={(v) => setMvForm({ ...mvForm, movement_type: v })}>
                      <SelectTrigger data-testid="mv-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{["IN", "OUT", "TRANSFER"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Quantity</Label><Input type="number" value={mvForm.quantity} onChange={(e) => setMvForm({ ...mvForm, quantity: e.target.value })} data-testid="mv-qty" /></div>
                  <div className="space-y-2"><Label>Unit cost</Label><Input type="number" value={mvForm.unit_cost} onChange={(e) => setMvForm({ ...mvForm, unit_cost: e.target.value })} /></div>
                </div>
                {mvForm.movement_type === "TRANSFER" && (
                  <div className="space-y-2"><Label>To warehouse</Label>
                    <Select value={mvForm.to_warehouse_id} onValueChange={(v) => setMvForm({ ...mvForm, to_warehouse_id: v })}>
                      <SelectTrigger data-testid="mv-to-wh"><SelectValue placeholder="Destination" /></SelectTrigger>
                      <SelectContent>{warehouses.filter((w) => w.id !== mvForm.warehouse_id).map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2"><Label>Batch (optional)</Label><Input value={mvForm.batch_number} onChange={(e) => setMvForm({ ...mvForm, batch_number: e.target.value })} /></div>
                <Button onClick={saveMovement} data-testid="mv-save">Post movement</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface-card p-4 overflow-x-auto">
          <h2 className="font-display font-bold mb-3">Live balances</h2>
          <Table>
            <TableHeader><TableRow><TableHead>SKU · Item</TableHead><TableHead>Warehouse</TableHead><TableHead>Qty</TableHead><TableHead>Avg cost</TableHead></TableRow></TableHeader>
            <TableBody>
              {balances.map((b) => (
                <TableRow key={b.id} data-testid={`stock-row-${b.id}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{b.item_name}</div>
                    <div className="text-xs font-mono text-muted-foreground">{b.item_sku} · {b.item_type}</div>
                  </TableCell>
                  <TableCell className="text-sm">{b.warehouse_name}</TableCell>
                  <TableCell className="font-mono">
                    {b.quantity}
                    {b.below_reorder && <span className="ml-2 inline-flex items-center text-xs text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />low</span>}
                  </TableCell>
                  <TableCell className="font-mono">₹{Number(b.avg_cost || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {balances.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No stock yet. Post an IN movement to start.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        <div className="surface-card p-4 overflow-x-auto">
          <h2 className="font-display font-bold mb-3">Recent movements</h2>
          <Table>
            <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Item</TableHead><TableHead>Type</TableHead><TableHead>Qty</TableHead></TableRow></TableHeader>
            <TableBody>
              {movements.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-xs font-mono">{new Date(m.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{m.item_sku}</TableCell>
                  <TableCell><Badge>{m.movement_type}</Badge></TableCell>
                  <TableCell className="font-mono">{m.quantity}</TableCell>
                </TableRow>
              ))}
              {movements.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No movements yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function Badge({ children }) {
  return <span className="inline-block px-2 py-0.5 rounded border border-border bg-secondary font-mono text-[10px]">{children}</span>;
}
